<template>
  <div class="wl" data-test="workflows-list-new">
    <FollowupsUiToggle
      target="old"
      page-key="workflow"
      note-text="Super Admin: this is the redesigned Workflows page."
      @toggle="$emit('switch-layout', 'old')"
    />

    <WorkflowListSkeleton v-if="pending" />

    <template v-else>
      <header class="wl__head">
        <div class="wl__titles">
          <h1 class="wl__h1">Workflows</h1>
          <p class="wl__lede">When something happens in Open Dental, a workflow can text or email the patient, assign your team a task, or update a Kanban board.</p>
        </div>
        <button
          v-if="hasWorkflows && canEditWorkflow"
          type="button"
          class="wl-btn wl-btn--primary"
          data-test="workflow-new-btn"
          @click="gotoCreatePage()"
        >
          <WfIcon name="plus" :size="16" :stroke="2.2" />
          New workflow
        </button>
      </header>

      <!-- ============================ the list ============================ -->
      <template v-if="hasWorkflows">
        <div class="wl-bar">
          <PillTabs :items="tabs" :value="tab" aria-label="Show workflows" @change="setTab" />
          <div class="wl-bar__filters">
            <label class="wl-search">
              <span class="wl-visually-hidden">Search workflows</span>
              <WfIcon class="wl-search__icon" name="search" :size="16" />
              <input
                v-model="searchTerm"
                type="text"
                class="wl-search__input"
                placeholder="Search workflows"
                autocomplete="off"
                data-test="workflows-search"
              >
            </label>
            <label class="wl-select">
              <span class="wl-visually-hidden">Filter by trigger</span>
              <select v-model="selectedTrigger" class="wl-select__input" data-test="workflows-trigger-filter">
                <option :value="null">All triggers</option>
                <option v-for="opt in triggerOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
              <WfIcon class="wl-select__chev" name="chev" :size="16" :stroke="2" />
            </label>
          </div>
        </div>

        <div class="wl-list">
          <div
            v-for="row in listRows"
            :key="row.workflow.WorkflowID"
            class="wl-row"
            :class="{ 'wl-row--paused': !row.workflow.Active }"
            data-test="workflow-row"
            :data-workflow-id="row.workflow.WorkflowID"
          >
            <div class="wl-row__switch">
              <FollowupsSwitch
                :value="!!row.workflow.Active"
                :label="row.workflow.Active ? 'Active' : 'Paused'"
                :aria-label="`Active: ${rowName(row.workflow)}`"
                :disabled="!canEditWorkflow || processingWorkflowId === row.workflow.WorkflowID"
                data-test="workflow-row-switch"
                @input="onSwitch(row.workflow, $event)"
              />
            </div>

            <div class="wl-row__main">
              <a
                v-if="canEditWorkflow"
                class="wl-row__name"
                :href="viewHref(row.workflow.WorkflowID)"
                data-test="workflow-row-name"
                @click="onNameClick($event, row.workflow.WorkflowID)"
              >{{ rowName(row.workflow) }}</a>
              <span v-else class="wl-row__name wl-row__name--plain" data-test="workflow-row-name">{{ rowName(row.workflow) }}</span>

              <div class="wl-row__flow">
                <span class="wl-chip wl-chip--trigger" data-test="workflow-trigger-chip">
                  <WfIcon :name="row.trigger.icon" :size="14" />{{ row.trigger.label }}
                </span>
                <template v-if="row.actions.length">
                  <span class="wl-row__arrow"><WfIcon name="arrowR" :size="14" :stroke="2.2" /></span>
                  <span v-for="action in row.actions" :key="action.key" class="wl-chip" data-test="workflow-action-chip">
                    <WfIcon :name="action.icon" :size="14" />{{ action.label }}
                  </span>
                </template>
              </div>
            </div>

            <div class="wl-row__actions">
              <span v-if="row.workflow.LastUpdated" class="wl-row__updated">Updated {{ row.workflow.LastUpdated | dateformat(dateFormatStr) }}</span>
              <button
                type="button"
                class="wl-btn wl-btn--secondary-sm"
                data-test="workflow-row-activity"
                @click="gotoWorkflowOutputPage(row.workflow.WorkflowID)"
              >
                <WfIcon name="activity" :size="15" :stroke="2" />
                View activity
              </button>
              <button
                v-if="canEditWorkflow"
                type="button"
                class="wl-iconbtn wl-iconbtn--edit"
                aria-label="Edit workflow"
                title="Edit workflow"
                data-test="workflow-row-edit"
                @click="gotoViewPage(row.workflow.WorkflowID)"
              >
                <WfIcon name="pencil" :size="16" />
              </button>
              <button
                v-if="$can('delete', 'workflow')"
                type="button"
                class="wl-iconbtn wl-iconbtn--delete"
                aria-label="Delete workflow"
                title="Delete workflow"
                data-test="workflow-row-delete"
                @click="askDelete(row.workflow)"
              >
                <WfIcon name="trash" :size="16" />
              </button>
            </div>
          </div>

          <div v-if="listRows.length === 0" class="wl-none" data-test="workflows-no-results">
            <div class="wl-none__title">{{ noResultsText }}</div>
            <button type="button" class="wl-none__clear" data-test="workflows-clear-filters" @click="clearFilters">Clear filters</button>
          </div>
        </div>

        <p class="wl__foot">Each trigger can run one workflow, so new actions for a trigger you already use go into its workflow.</p>
      </template>

      <!-- ============================ empty ============================ -->
      <div v-else class="wl-empty-wrap">
        <div class="wl-empty" data-test="workflows-empty">
          <span class="wl-empty__tile"><WfIcon name="workflow" :size="24" :stroke="1.8" /></span>
          <h2 class="wl-empty__h2">Let Teamio handle the routine follow-ups</h2>
          <p class="wl-empty__p">A workflow starts when something happens in Open Dental, like an appointment being completed. Then it does the routine part for you: texts or emails the patient, or gives your team a task.</p>
          <div class="wl-empty__example">
            <span class="wl-empty__example-label">For example</span>
            <span class="wl-chip wl-chip--trigger"><WfIcon name="calCheck" :size="14" />Appointment completed</span>
            <span class="wl-row__arrow"><WfIcon name="arrowR" :size="14" :stroke="2.2" /></span>
            <span class="wl-chip"><WfIcon name="msg" :size="14" />Send text message</span>
            <span class="wl-chip"><WfIcon name="clipCheck" :size="14" />Assign Teamio task</span>
          </div>
          <button
            v-if="canEditWorkflow"
            type="button"
            class="wl-btn wl-btn--primary wl-btn--lg"
            data-test="workflow-new-btn"
            @click="gotoCreatePage()"
          >
            <WfIcon name="plus" :size="16" :stroke="2.2" />
            New workflow
          </button>
        </div>
      </div>
    </template>

    <!-- ========================= delete dialog ========================= -->
    <div
      v-if="pendingDelete"
      class="wl-scrim"
      data-test="workflow-delete-dialog"
      @click.self="cancelDelete"
      @keydown.esc.stop.prevent="cancelDelete"
    >
      <div class="wl-dialog" role="dialog" aria-modal="true" aria-labelledby="wl-del-title" aria-describedby="wl-del-text">
        <div id="wl-del-title" class="wl-dialog__title">Delete {{ deleteName }}?</div>
        <p id="wl-del-text" class="wl-dialog__text">It stops running, and its blocks and rules are deleted with it. This can’t be undone.</p>
        <div class="wl-dialog__actions">
          <button
            ref="cancelBtn"
            type="button"
            class="wl-btn wl-btn--ghost"
            data-test="workflow-delete-cancel"
            :disabled="deleting"
            @click="cancelDelete"
          >Cancel</button>
          <button
            type="button"
            class="wl-btn wl-btn--danger"
            data-test="workflow-delete-confirm"
            :disabled="deleting"
            @click="del"
          >Delete workflow</button>
        </div>
      </div>
    </div>

    <!-- A confirmation or a failed delete / switch, one at a time (replaces the toasts). -->
    <WorkflowNotice :notice="notice" @action="runNoticeAction" @close="closeNotice" />
  </div>
</template>

<script>
/**
 * The redesigned Workflows LIST (design "Workflows UI redesign", frames 1a list / 1b empty / 1t tablet;
 * Workflow overhaul P2, 2026-10-10). Resume doc: Documents/workflow-ui-overhaul.md.
 *
 * `extends` the classic WorkflowList.vue, which is NOT edited. EVERY rule is the classic code, inherited as
 * it is: the load (init), the status / search / trigger filters (filteredWorkflows), the counts, the
 * Active switch that saves itself (the `enabled` watcher -> toggleWorkflowActive -> enable / disableWorkflow,
 * with its processingWorkflowId lock and its revert on failure), the actions parsed out of FlowyText
 * (parseActionsFromFlowyText), the delete store action and "View activity". Only the shell is new.
 *
 * What is NOT here, on purpose: the template modal, the template cards and "Suggested workflows" (templates
 * are gone, "New workflow" opens the builder directly) and the 10-per-page pagination (every workflow is
 * drawn). The inherited data for them stays, unused.
 *
 * Differences from the classic page that are decisions, not accidents:
 *   - "New workflow", the name and the pencil open the builder in THIS tab (the classic opened a new one).
 *     "View activity" still opens a new tab: that page is not redesigned.
 *   - Delete asks in an in-page dialog (no browser dialog) and takes the row out of the list in place;
 *     the classic reloaded the whole page.
 *   - The row labels come from the design's catalog (src/utils/workflow/blockCatalog.js). getTriggerLabel is
 *     overridden to read the same catalog so the search box matches the chip the row shows.
 *
 * ⛔ Deleting a row changes the `enabled` array the classic watcher reads. Its handler finds "the first row
 * whose Active differs" and saves THAT row, so removing a row from the middle would enable or disable a
 * neighbour. del() raises suppressToggleSync until the watcher has run, and toggleWorkflowActive ignores
 * calls made while it is up. Pinned by tests/component/WorkflowsListPage.spec.js.
 *
 * Notices (P6): nothing here draws a toast. A delete that worked is a short dark note ("Deleted <name>"); a
 * delete or a switch that did not go through is a card with "Try again" (WorkflowNotice, state in noticeHost.js).
 * toggleWorkflowActive is the classic method written out again so the failure can say what to do; it keeps the
 * classic lock, store calls and revert. The revert is made while suppressToggleSync is up, otherwise the
 * inherited watcher would read the switch flipping back as a second click and save it again.
 *
 * ⛔ No prop on this page: a prop named patNum / siteID / loading / allSettings would be silently replaced by
 * the mixin's computed of that name (mixin-prop-shadow-guardrail.test.js).
 */
import WorkflowList from '../WorkflowList.vue';
import WorkflowListSkeleton from '../WorkflowListSkeleton.vue';
import PillTabs from '@/components/PatientPanel/actions/PillTabs.vue';
import FollowupsSwitch from '@/components/Followups/FollowupsSwitch.vue';
import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
import {
  resolveAction, actionLabel, actionIcon, triggerLabel, triggerIcon,
} from '@/utils/workflow/blockCatalog.js';
import WfIcon from './WfIcon.vue';
import WorkflowNotice from './WorkflowNotice.vue';
import noticeHost from './noticeHost.js';
import { isAutoName } from '@/utils/workflow/saveBlockers.js';

const RETRY_HINT = 'Check your internet connection, then try again.';

export default {
  name: 'WorkflowsListPage',

  extends: WorkflowList,

  mixins: [noticeHost],

  components: {
    WorkflowListSkeleton, PillTabs, FollowupsSwitch, FollowupsUiToggle, WfIcon, WorkflowNotice,
  },

  data() {
    return {
      /** True once the first load finished, so "no workflows yet" is never drawn while settings are loading. */
      loadedOnce: false,
      /** The workflow the delete dialog is asking about (null = closed). */
      pendingDelete: null,
      deleting: false,
      /** Up while a delete is changing the list, so the inherited `enabled` watcher saves nothing. */
      suppressToggleSync: false,
      /** The element that had focus before the dialog opened. */
      returnFocusTo: null,
    };
  },

  computed: {
    hasWorkflows() {
      return this.workflows.length > 0;
    },

    pending() {
      return this.showOverlay || !this.loadedOnce;
    },

    /** statusFilter is null / 'active' / 'inactive'; the tabs are All / Active / Paused. */
    tab() {
      return this.statusFilter || 'all';
    },

    tabs() {
      return [
        { id: 'all', label: 'All', count: this.workflows.length },
        { id: 'active', label: 'Active', count: this.activeCount },
        { id: 'inactive', label: 'Paused', count: this.inactiveCount },
      ];
    },

    /** The triggers in use, in the order the workflows list them. */
    triggerOptions() {
      return this.availableTriggers.map(t => ({ value: t, label: triggerLabel(t) }));
    },

    /** Every filtered workflow with its chips resolved (no pagination). */
    listRows() {
      return this.filteredWorkflows.map(workflow => ({
        workflow,
        trigger: { label: triggerLabel(workflow.TriggerOn), icon: triggerIcon(workflow.TriggerOn) },
        actions: this.actionChips(workflow),
      }));
    },

    noResultsText() {
      const q = (this.searchTerm || '').trim();
      if (q) return `No workflows match “${q}”`;
      if (this.selectedTrigger) return `No workflows use ${triggerLabel(this.selectedTrigger)}`;
      return this.statusFilter === 'active' ? 'No active workflows' : 'No paused workflows';
    },

    deleteName() {
      return this.pendingDelete ? this.pendingDelete.Name : '';
    },
  },

  watch: {
    pendingDelete(open) {
      if (open) {
        this.returnFocusTo = typeof document !== 'undefined' ? document.activeElement : null;
        // Cancel is the safe default for a destructive question; it also puts focus inside the dialog so
        // Escape reaches it.
        this.$nextTick(() => {
          const btn = this.$refs.cancelBtn;
          if (btn && typeof btn.focus === 'function') btn.focus();
        });
      } else if (this.returnFocusTo && typeof this.returnFocusTo.focus === 'function') {
        this.returnFocusTo.focus();
        this.returnFocusTo = null;
      }
    },
  },

  methods: {
    // ---- load ---------------------------------------------------------------------------------
    async init() {
      try {
        await WorkflowList.methods.init.call(this);
      } finally {
        this.loadedOnce = true;
      }
    },

    // ---- labels -------------------------------------------------------------------------------
    /** The classic filter searches this label; read the catalog so it matches the chip on screen. */
    getTriggerLabel(triggerOn) {
      return triggerLabel(triggerOn);
    },

    /**
     * What the row and its notices call the workflow: its name, unless the builder made that name from the
     * trigger (saveBlockers `autoName`, e.g. `appointment-scheduled`): then the trigger's label, as the builder shows.
     */
    rowName(workflow) {
      if (!workflow) return '';
      const label = this.getTriggerLabel(workflow.TriggerOn);
      return label && isAutoName(workflow.Name, label) ? label : (workflow.Name || '');
    },

    /** One neutral chip per distinct action in the workflow's FlowyText. */
    actionChips(workflow) {
      const seen = {};
      const chips = [];
      this.parseActionsFromFlowyText(workflow.FlowyText).forEach((raw) => {
        const entry = resolveAction(raw);
        const key = entry ? entry.type : String(raw);
        if (seen[key]) return;
        seen[key] = true;
        chips.push({ key, label: actionLabel(raw), icon: actionIcon(raw) });
      });
      return chips;
    },

    // ---- filters ------------------------------------------------------------------------------
    setTab(id) {
      this.statusFilter = id === 'active' || id === 'inactive' ? id : null;
    },

    clearFilters() {
      this.searchTerm = '';
      this.selectedTrigger = null;
      this.statusFilter = null;
    },

    // ---- the Active switch --------------------------------------------------------------------
    /** Flip Active; the inherited `enabled` watcher saves it (and puts it back if the save fails). */
    onSwitch(workflow, value) {
      if (!this.canEditWorkflow || this.processingWorkflowId === workflow.WorkflowID) return;
      workflow.Active = !!value;
    },

    /**
     * The store actions raise the app-wide green bar ("Workflow enabled / disabled successfully!") by themselves.
     * The switch already shows the result here, and a failure gets a notice, so ask the store to stay quiet.
     */
    enableWorkflow(payload) {
      return this.$store.dispatch('workflow/enableWorkflow', Object.assign({}, payload, { skipAlertLoading: true }));
    },
    disableWorkflow(payload) {
      return this.$store.dispatch('workflow/disableWorkflow', Object.assign({}, payload, { skipAlertLoading: true }));
    },

    /**
     * The classic method, written out: the same lock, the same enable / disable store calls, the same revert of
     * `workflow.Active` on failure. Only the failure message differs (a notice with "Try again", not a toast), and
     * the revert is made with the watcher held off so it is not saved a second time.
     */
    async toggleWorkflowActive(workflow) {
      if (this.suppressToggleSync || !workflow) return;
      if (this.processingWorkflowId === workflow.WorkflowID) return;

      this.processingWorkflowId = workflow.WorkflowID;
      this.closeNotice();
      const turningOn = !!workflow.Active;

      try {
        if (turningOn) {
          await this.enableWorkflow({ WorkflowID: workflow.WorkflowID });
        } else {
          await this.disableWorkflow({ WorkflowID: workflow.WorkflowID });
        }
      } catch (err) {
        console.error('Error toggling workflow:', err);
        // Put the switch back. The inherited `enabled` watcher runs in the next flush and would take the revert
        // for a click of its own, so hold it off until it has run.
        this.suppressToggleSync = true;
        workflow.Active = !workflow.Active;
        this.showNotice({
          kind: 'error',
          title: `Couldn’t ${turningOn ? 'turn on' : 'pause'} ${this.rowName(workflow)}`,
          text: `The switch is back where it was. ${RETRY_HINT}`,
          actionLabel: 'Try again',
        }, () => this.retryToggle(workflow.WorkflowID, turningOn));
        await this.$nextTick();
        this.suppressToggleSync = false;
      } finally {
        this.processingWorkflowId = null;
      }
    },

    /** "Try again": flip the switch the way the person did. The inherited watcher does the one save. */
    retryToggle(workflowID, value) {
      const current = this.workflows.find(w => w.WorkflowID === workflowID);
      if (current) this.onSwitch(current, value);
    },

    // ---- navigation (same tab) ----------------------------------------------------------------
    viewHref(workflowID) {
      try {
        const resolved = this.$router.resolve({ name: 'ViewWorkflow2', params: { workflowID: String(workflowID) } });
        return resolved && resolved.href ? resolved.href : '#';
      } catch (e) {
        return '#';
      }
    },

    /** A plain click opens the builder here; ctrl / cmd / shift / middle click keeps the browser's new tab. */
    onNameClick(event, workflowID) {
      if (event && (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0)) return;
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      this.gotoViewPage(workflowID);
    },

    gotoCreatePage() {
      if (!this.assertCan('update', 'workflow')) return;
      this.$router.push({ name: 'CreateWorkflow2' });
    },

    gotoViewPage(workflowID) {
      // Silent, as the classic: the name and the pencil are only drawn for someone who can update.
      if (!this.$can('update', 'workflow')) return;
      this.$router.push({ name: 'ViewWorkflow2', params: { workflowID: String(workflowID) } });
    },

    // ---- delete -------------------------------------------------------------------------------
    askDelete(workflow) {
      if (!this.assertCan('delete', 'workflow')) return;
      this.selectedWorkflowID = workflow.WorkflowID;
      this.pendingDelete = workflow;
    },

    cancelDelete() {
      if (this.deleting) return;
      this.pendingDelete = null;
    },

    /** The dialog's "Delete workflow". */
    async del() {
      await this.deleteRow(this.pendingDelete);
    },

    /** "Try again" after a failed delete: the dialog stays shut, the same workflow is deleted again. */
    retryDelete(workflowID) {
      const current = this.workflows.find(w => w.WorkflowID === workflowID);
      if (current) this.deleteRow(current);
    },

    async deleteRow(target) {
      if (!this.assertCan('delete', 'workflow')) return;
      if (!target || this.deleting) return;
      this.deleting = true;
      this.closeNotice();
      try {
        await this.deleteWorkflow({ WorkflowID: target.WorkflowID });
        // The row leaves the list in place (no page reload); keep the Active watcher out of it.
        this.suppressToggleSync = true;
        this.workflows = this.workflows.filter(w => w.WorkflowID !== target.WorkflowID);
        this.showNotice({ kind: 'ok', text: `Deleted ${this.rowName(target)}` });
        await this.$nextTick();
      } catch (e) {
        console.error('Error deleting workflow:', e);
        this.showNotice({
          kind: 'error',
          title: `Couldn’t delete ${this.rowName(target)}`,
          text: `It’s still in your list. ${RETRY_HINT}`,
          actionLabel: 'Try again',
        }, () => this.retryDelete(target.WorkflowID));
      } finally {
        this.suppressToggleSync = false;
        this.deleting = false;
        this.pendingDelete = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './workflow-tokens';

.wl {
  @include wf-page-root;
  position: relative;
  max-width: $wf-max-width;
  margin: 0 auto;
  padding-bottom: 40px;
}

.wl-visually-hidden {
  @include fu-visually-hidden;
}

// ---- Buttons -------------------------------------------------------------------------------
.wl-btn {
  &--primary { @include fu-btn(primary, md); }
  &--lg { height: 48px; padding: 0 22px; font-size: 16px; }
  &--secondary-sm { @include fu-btn(secondary, sm); }
  &--ghost { @include fu-btn(ghost, md); }
  &--danger { @include fu-btn(danger, md); }
}

.wl-iconbtn {
  @include fu-icon-btn(36px);

  &--delete:hover:not([disabled]) {
    background: $fu-danger-subtle;
    color: $fu-danger-text;
  }
}

// ---- Header --------------------------------------------------------------------------------
.wl__head {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 16px 24px;
}

.wl__titles {
  flex: 1 1 360px;
  min-width: 0;
}

.wl__h1 {
  margin: 0;
  font-family: $fu-font-display;
  font-weight: 800;
  font-size: 40px;
  letter-spacing: -0.03em;
  line-height: 1.05;
  color: $fu-text;

  @include wf-phone { font-size: 32px; }
}

.wl__lede {
  margin: 8px 0 0;
  max-width: 600px;
  font-size: 15px;
  line-height: 1.5;
  color: $fu-neutral-500;
}

// ---- Toolbar -------------------------------------------------------------------------------
.wl-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 12px;
  margin: 26px 0 14px;
}

.wl-bar__filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  flex: 1 1 420px;
  min-width: 0;
  margin-left: auto;
}

.wl-search {
  position: relative;
  display: block;
  flex: 1 1 220px;
  max-width: 300px;
  min-width: 0;
  margin: 0;

  @include wf-phone { max-width: none; }
}

.wl-search__input {
  @include fu-input(40px);
  padding-left: 38px;

  // iOS Safari zooms in on a focused field under 16px and never zooms back: phones and iPads (<= 1024px) get 16px, in px.
  @include wf-touch { font-size: 16px; }
}

.wl-search__icon {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  color: $fu-neutral-400;
  pointer-events: none;
}

.wl-select {
  position: relative;
  display: block;
  flex: 0 0 210px;
  margin: 0;

  @include wf-phone { flex: 1 1 100%; }
}

.wl-select__input {
  @include fu-input(40px);
  padding-right: 34px;
  cursor: pointer;

  // Same iOS focus-zoom rule as the search box above.
  @include wf-touch { font-size: 16px; }
}

.wl-select__chev {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  color: $fu-neutral-500;
  pointer-events: none;
}

// ---- The list ------------------------------------------------------------------------------
.wl-list {
  @include fu-card(14px);
  overflow: hidden;
}

.wl-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
  padding: 16px 20px;

  &:hover { background: $fu-neutral-25; }
  & + & { border-top: 1px solid $fu-border; }
}

.wl-row__switch {
  flex: none;
  width: 100px;
}

.wl-row__main {
  flex: 1 1 360px;
  min-width: 0;
}

.wl-row__name {
  display: inline-block;
  max-width: 100%;
  font-family: $fu-font-display;
  font-weight: 800;
  font-size: 16.5px;
  letter-spacing: -0.01em;
  color: $fu-text;
  overflow-wrap: anywhere;
  text-decoration: none;

  &:hover { color: $fu-text; text-decoration: underline; }
  &--plain:hover { text-decoration: none; }
}

.wl-row--paused .wl-row__name { color: $fu-neutral-600; }

.wl-row__flow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.wl-row__arrow {
  display: flex;
  color: $fu-neutral-400;
}

.wl-chip {
  @include wf-chip(action);

  &--trigger { @include wf-chip(trigger); }
}

.wl-row__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-left: auto;

  // Rows wrap as in frame 1t: the actions drop under the name, in line with it (switch 100px + gap 20px).
  @include wf-tablet { margin-left: 120px; }
  @include wf-phone { margin-left: 0; }
}

.wl-row__updated {
  margin-right: 10px;
  font-size: 12.5px;
  color: $fu-neutral-500;
  white-space: nowrap;
}

.wl-none {
  padding: 40px 20px;
  text-align: center;
}

.wl-none__title {
  font-size: 15px;
  font-weight: 700;
  color: $fu-text;
}

.wl-none__clear {
  @include fu-link-btn;
  margin-top: 6px;
  font-size: 13.5px;
  font-weight: 700;
}

.wl__foot {
  margin: 12px 0 0;
  font-size: 12.5px;
  color: $fu-neutral-500;
}

// ---- Empty state ---------------------------------------------------------------------------
.wl-empty-wrap {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}

.wl-empty {
  @include fu-card(20px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  width: 100%;
  max-width: 620px;
  padding: 44px 40px 40px;
  text-align: center;

  @include wf-phone { padding: 32px 20px; }
}

.wl-empty__tile {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: $fu-brand-50;
  color: $fu-brand-700;
}

.wl-empty__h2 {
  margin: 4px 0 0;
  font-family: $fu-font-display;
  font-weight: 800;
  font-size: 26px;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: $fu-text;
}

.wl-empty__p {
  margin: 0;
  max-width: 470px;
  font-size: 15px;
  line-height: 1.55;
  color: $fu-neutral-600;
}

.wl-empty__example {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 12px 14px;
  border-radius: 12px;
  background: $fu-neutral-50;
}

.wl-empty__example-label {
  margin-right: 4px;
  font-size: 12.5px;
  font-weight: 600;
  color: $fu-neutral-500;
}

.wl-empty .wl-btn--lg { margin-top: 6px; }

// ---- Delete dialog -------------------------------------------------------------------------
.wl-scrim {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: $wf-z-dialog;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(12, 15, 14, 0.32);
}

.wl-dialog {
  width: 440px;
  max-width: 100%;
  padding: 24px;
  border-radius: 20px;
  background: $fu-surface;
  box-shadow: $fu-shadow-xl;
}

.wl-dialog__title {
  font-family: $fu-font-display;
  font-weight: 800;
  font-size: 22px;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: $fu-text;
  overflow-wrap: anywhere;
}

.wl-dialog__text {
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.55;
  color: $fu-neutral-600;
}

.wl-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 22px;
}
</style>
